// Word-level changes inside a modified block (SPEC §6 step 4), GitHub-style:
// insertions wrapped in <ins>, deletions shown inline as <del> with the old words.
// Works on the SANITIZED head tree and only adds ins/del elements holding plain text
// (React escapes it), so no HTML is produced after the sanitizer.
import { diffWordsWithSpace } from 'diff';
import type { Root, Element, ElementContent, Text } from 'hast';

const BLOCKISH = new Set(['p', 'li', 'td', 'th', 'tr', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'pre', 'blockquote', 'summary', 'section', 'ul', 'ol', 'table']);
const SEP = '\n';

type Segment = { node: Text; start: number; end: number };

/** Concatenated text with a separator after block-ish elements (so words don't merge across them). */
function flatten(root: Root | Element): { text: string; segments: Segment[] } {
  let text = '';
  const segments: Segment[] = [];
  const visit = (n: Root | Element) => {
    for (const c of n.children) {
      if (c.type === 'text') { segments.push({ node: c, start: text.length, end: text.length + c.value.length }); text += c.value; }
      else if (c.type === 'element') {
        visit(c);
        if (BLOCKISH.has(c.tagName) && !text.endsWith(SEP)) text += SEP;
      }
    }
  };
  visit(root);
  return { text, segments };
}

const el = (tagName: 'ins' | 'del', value: string): Element =>
  ({ type: 'element', tagName, properties: { className: ['hq-word'] }, children: [{ type: 'text', value }] });

export type WordDiffResult = { hast: Root; changed: boolean };

/** Returns a copy of `head` with word changes against `base` marked. */
export function markWordChanges(base: Root, head: Root): WordDiffResult {
  const out = structuredClone(head);
  const b = flatten(base);
  const h = flatten(out);

  // Changes in head-text coordinates.
  const inserted: [number, number][] = [];
  const deletions = new Map<number, string>(); // head offset → deleted text
  let at = 0;
  let gapAfterInsert = false; // only whitespace unchanged since the last insertion
  for (const part of diffWordsWithSpace(b.text, h.text)) {
    if (part.added) {
      const last = inserted.at(-1);
      // "14" + " " + "calendar" reads as one insertion: merge across whitespace-only gaps.
      if (last && (last[1] === at || gapAfterInsert)) last[1] = at + part.value.length;
      else inserted.push([at, at + part.value.length]);
      at += part.value.length;
      gapAfterInsert = true;
    } else if (part.removed) {
      const v = part.value.replaceAll(SEP, ' ');
      if (v.trim()) deletions.set(at, (deletions.get(at) ?? '') + v);
    } else {
      at += part.value.length;
      gapAfterInsert = gapAfterInsert && /^[ \t]+$/.test(part.value);
    }
  }
  if (!inserted.length && !deletions.size) return { hast: out, changed: false };

  // Deletions at a separator (between blocks) attach to the start of the next text node.
  const starts = h.segments.map((s) => s.start);
  const pending = new Map<Segment, Map<number, string>>();
  for (const [offset, value] of deletions) {
    let seg = h.segments.find((s) => offset >= s.start && offset < s.end);
    let local: number;
    if (seg) local = offset - seg.start;
    else {
      const next = starts.findIndex((st) => st >= offset);
      seg = next >= 0 ? h.segments[next] : h.segments.at(-1);
      if (!seg) continue;
      local = next >= 0 ? 0 : seg.end - seg.start;
    }
    const m = pending.get(seg) ?? new Map<number, string>();
    m.set(local, (m.get(local) ?? '') + value);
    pending.set(seg, m);
  }

  const replace = new Map<Text, ElementContent[]>();
  for (const seg of h.segments) {
    const ins = inserted.filter(([s, e]) => s < seg.end && e > seg.start).map(([s, e]) => [Math.max(s, seg.start) - seg.start, Math.min(e, seg.end) - seg.start] as const);
    const dels = pending.get(seg);
    if (!ins.length && !dels) continue;
    const cuts = new Set<number>([0, seg.end - seg.start]);
    for (const [s, e] of ins) { cuts.add(s); cuts.add(e); }
    for (const k of dels?.keys() ?? []) cuts.add(k);
    const points = [...cuts].sort((x, y) => x - y);
    const pieces: ElementContent[] = [];
    const value = seg.node.value;
    points.forEach((p, i) => {
      const d = dels?.get(p);
      if (d) pieces.push(el('del', d));
      const next = points[i + 1];
      if (next === undefined) return;
      const chunk = value.slice(p, next);
      if (!chunk) return;
      const isIns = ins.some(([s, e]) => p >= s && next <= e);
      pieces.push(isIns ? el('ins', chunk) : { type: 'text', value: chunk });
    });
    replace.set(seg.node, pieces);
  }

  const rewrite = (n: Root | Element) => {
    n.children = n.children.flatMap((c) => {
      if (c.type === 'text' && replace.has(c)) return replace.get(c)!;
      if (c.type === 'element') rewrite(c);
      return [c];
    }) as typeof n.children;
  };
  rewrite(out);
  return { hast: out, changed: true };
}
